import { acceptedVideoCodecs, audioExtensions, backgroundExtensions, backgroundBinaryFileTypes, logoExtensions } from '@/static/file-types'
import FFmpegUtility from '@/utils/ffmpeg-utility'

export class ValidationError extends Error {
  private _title: string
  constructor (title: string, message?: string) {
    super(message)
    this._title = title
  }

  get title (): string {
    return this._title
  }
}

export function useFileValidator () {
  const validateAudioFileExtension = (file: File): void => {
    const hasSupportedExtension = audioExtensions.some((extension) => file.name.toLowerCase().endsWith(extension))
    if (!hasSupportedExtension) {
      throw new ValidationError('Invalid audio file type', 'Try again using a MP3 or WAV file')
    }
  }

  const loadFileBinary = async (file: File) : Promise<ArrayBuffer> => {
    const blob = file.slice(0, 4)
    return await blob.arrayBuffer()
  }

  const validateBackgroundFile = async (file: File): Promise<void> => {
    const supportedExtension = backgroundExtensions.find(ext => file.name.toLowerCase().endsWith(ext))
    // calculate the magic number of file to determine file type
    const binary = await loadFileBinary(file)
    const uint = new Uint8Array(binary)
    // iterate the Uint8Array; convert each byte to a string of hexadecimal characters
    const hex = uint.reduce((prevString, byte) => prevString +  byte.toString(16).toUpperCase(), '')
    const hasSupportedHex = backgroundBinaryFileTypes.includes(hex)
       
    if (!supportedExtension || !hasSupportedHex ) {
      throw new ValidationError('Invalid background file type', 'Try again using a GIF, MP4, MOV, JPEG or PNG file')
    } else if (file.size > 100000000) /* > 100MB */ {
      throw new ValidationError('Background file too large', 'Try again with a file smaller than 100MB')
    }

    if (file.type.startsWith('video/')) {
      const codecString =  await FFmpegUtility.getInstance().getVideoCodec(file, supportedExtension)
      const hasValidCodec = acceptedVideoCodecs.some((acceptedCodec) => codecString.includes(acceptedCodec))

      if (!hasValidCodec) {
        throw new ValidationError('Oops something went wrong')
      }
    }
  }

  const validateStickerFile = (file: File): void => {
    const hasSupportedExtension = logoExtensions.some(ext => file.name.toLowerCase().endsWith(ext))
    if (!hasSupportedExtension) {
      throw new ValidationError('Invalid sticker file type', 'Try again using a GIF, JPEG or PNG file')
    }
  }

  const validateAudioFileLength = (file: File, duration: number): void => {
    if (file.size > 200000000 || duration > 600) { // 200MB
      throw new ValidationError('Audio file too large', 'Try again using a file less than 10min duration and 200MB in size.')
    }
  }

  return {
    validateAudioFileExtension,
    validateBackgroundFile,
    validateStickerFile,
    validateAudioFileLength
  }
}
